// Artlines custom pair: second shirt with the same custom design.
// Rendered by snippets/artlines-custom-pair.liquid; used by assets/product-form.js on submit.
// Pricing and free shipping are enforced at checkout by the Artlines Bundle Discounts app.
if (!customElements.get('artlines-custom-pair')) {
  customElements.define(
    'artlines-custom-pair',
    class ArtlinesCustomPair extends HTMLElement {
      connectedCallback() {
        this.form = this.closest('form');
        this.toggle = this.querySelector('[data-al-custom-pair-toggle]');
        this.options = this.querySelector('[data-al-custom-pair-options]');
        this.sizeSelect = this.querySelector('[data-al-pair-size]');
        this.colorSelect = this.querySelector('[data-al-pair-color]');
        this.error = this.querySelector('[data-al-pair-error]');
        this.sizeIndex = Number(this.dataset.sizeIndex);
        this.colorIndex = Number(this.dataset.colorIndex);

        try {
          this.variants = JSON.parse(this.querySelector('[data-al-pair-variants]').textContent);
        } catch (e) {
          this.variants = [];
        }

        this.toggle.addEventListener('change', () => this.onToggle());
        this.sizeSelect.addEventListener('change', () => this.validate());
        this.colorSelect.addEventListener('change', () => this.validate());
        this.applyUrlPresets();
      }

      // Links from the menu, homepage and vehicle finder can preset the page:
      //   ?pair=1  -> tick "add a second shirt"
      //   ?car=... -> prefill Car Model (Upload-Lift renders that field after load, so wait for it)
      applyUrlPresets() {
        const params = new URLSearchParams(window.location.search);

        if (params.get('pair') === '1' && !this.toggle.checked) {
          this.toggle.checked = true;
          this.onToggle();
        }

        const car = (params.get('car') || '').trim().slice(0, 120);
        if (!car || !this.form) return;
        let attempts = 0;
        const fill = () => {
          const input = this.form.querySelector('input[name="properties[Car Model]"]');
          if (input) {
            if (!input.value) {
              input.value = car;
              input.dispatchEvent(new Event('input', { bubbles: true }));
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
          }
          if (++attempts < 40) setTimeout(fill, 250);
        };
        fill();
      }

      get quantityInput() {
        // form.id would return the <input name="id"> (named-element shadowing), so read the attribute.
        const formId = this.form?.getAttribute('id');
        if (!formId) return null;
        return document.querySelector(`input[name="quantity"][form="${CSS.escape(formId)}"]`);
      }

      isActive() {
        return this.toggle.checked;
      }

      onToggle() {
        const active = this.isActive();
        this.options.hidden = !active;
        const quantity = this.quantityInput;

        if (active) {
          this.matchMainSelection();
          if (quantity) {
            quantity.value = 1;
            quantity.readOnly = true;
            quantity.closest('quantity-input')?.classList.add('al-custom-pair--locked');
          }
        } else if (quantity) {
          quantity.readOnly = false;
        }
        this.validate();
      }

      // Default the second shirt to the size/color chosen for the first one.
      matchMainSelection() {
        const main = this.mainVariant();
        if (!main) return;
        this.sizeSelect.value = main.options[this.sizeIndex];
        this.colorSelect.value = main.options[this.colorIndex];
      }

      mainVariant() {
        const id = Number(this.form?.querySelector('[name="id"]')?.value);
        return this.variants.find((variant) => variant.id === id);
      }

      secondVariant() {
        const size = this.sizeSelect.value;
        const color = this.colorSelect.value;
        return this.variants.find(
          (variant) => variant.options[this.sizeIndex] === size && variant.options[this.colorIndex] === color
        );
      }

      validate() {
        const variant = this.secondVariant();
        const ok = !this.isActive() || Boolean(variant && variant.available);
        this.error.hidden = ok;
        return ok;
      }

      newDesignId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      }

      // Converts the product form's FormData into a two-line /cart/add JSON payload.
      // Returns null when the pair can't be built (caller shows the error).
      buildCartPayload(formData) {
        if (!this.validate()) return null;
        const second = this.secondVariant();
        const mainId = Number(formData.get('id'));
        if (!mainId || !second) return null;

        const properties = {};
        let sections;
        let sectionsUrl;
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
          else if (key === 'sections') sections = value;
          else if (key === 'sections_url') sectionsUrl = value;
        }

        const designId = this.newDesignId();
        const payload = {
          items: [
            {
              id: mainId,
              quantity: 1,
              properties: { ...properties, _al_design_id: designId, _al_bundle_role: 'primary' },
            },
            {
              id: second.id,
              quantity: 1,
              properties: {
                ...properties,
                'Bundle': '2nd shirt (same design)',
                _al_design_id: designId,
                _al_bundle_role: 'second',
              },
            },
          ],
        };
        if (sections) payload.sections = sections;
        if (sectionsUrl) payload.sections_url = sectionsUrl;
        return payload;
      }
    }
  );
}
